import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { getSession } from '~/lib/auth';
import { reviewFlip, reviewPack, reviewQueue } from '~/lib/studio';
import { buildHead } from '~/seo/site';
import { WaveformPlayer } from '~/ui/WaveformPlayer';
import { Badge } from '~/ui/components/badge';
import { Button } from '~/ui/components/button';

export const Route = createFileRoute('/studio/review')({
  // A&R + admin only.
  beforeLoad: async ({ location }) => {
    const s = await getSession();
    if (!s) throw redirect({ to: '/auth/sign-in', search: { next: location.pathname } });
    if (s.role !== 'ar' && s.role !== 'admin')
      throw redirect({ to: '/studio', search: { tab: 'genres' } });
  },
  head: () =>
    buildHead({
      title: 'Studio review',
      description: 'Approve community submissions.',
      path: '/studio/review',
    }),
  component: Review,
});

function Review() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['review', 'queue'], queryFn: reviewQueue });
  const decideFlip = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) => reviewFlip(id, approve),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['review', 'queue'] });
      qc.invalidateQueries({ queryKey: ['studio', 'flips'] });
    },
  });
  const decidePack = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) => reviewPack(id, approve),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['review', 'queue'] });
      qc.invalidateQueries({ queryKey: ['studio', 'packs'] });
    },
  });

  const flips = data?.flips ?? [];
  const packs = data?.packs ?? [];
  const empty = !isLoading && flips.length === 0 && packs.length === 0;

  return (
    <section className="mx-auto max-w-3xl px-4 py-10 space-y-8">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Review queue</h1>
          <Link to="/studio" search={{ tab: 'genres' }} className="text-sm underline">
            Back to Studio
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          Community submissions awaiting approval. Approving promotes them into the shared pool.
        </p>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
      {empty && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nothing to review. Nice and clean.
        </div>
      )}

      {flips.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xl font-semibold">Sample Flips ({flips.length})</h2>
          {flips.map((f) => (
            <div key={f.id} className="rounded-lg border border-border bg-card p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{f.label}</span>
                  {f.genreSlug && <Badge variant="secondary">{f.genreSlug}</Badge>}
                </div>
                <span className="text-xs text-muted-foreground">
                  by @{f.submitterHandle ?? 'unknown'} · {f.voteCount} votes
                </span>
              </div>
              <WaveformPlayer src={f.url} />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={decideFlip.isPending}
                  onClick={() => decideFlip.mutate({ id: f.id, approve: true })}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decideFlip.isPending}
                  onClick={() => decideFlip.mutate({ id: f.id, approve: false })}
                >
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {packs.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xl font-semibold">Uploaded packs ({packs.length})</h2>
          {packs.map((p) => (
            <div key={p.id} className="rounded-lg border border-border bg-card p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{p.name}</span>
                  <Badge variant="secondary">{p.genreSlug}</Badge>
                  <span className="text-xs text-muted-foreground">{p.stemCount} stems</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  by @{p.submitterHandle ?? 'unknown'} · {p.voteCount} votes
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={decidePack.isPending}
                  onClick={() => decidePack.mutate({ id: p.id, approve: true })}
                >
                  Approve to pool
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decidePack.isPending}
                  onClick={() => decidePack.mutate({ id: p.id, approve: false })}
                >
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
